import { useState, useEffect } from 'react';
import { ApiModel, ApiModelParameter } from '../components/models/apiTypes';
import { useImageModels } from './useImageModels';
import api from '../utils/api';

// Cache for model data to avoid redundant API calls
const modelCache = new Map<string, ApiModel>();

/**
 * Hook to get model parameters for a specific model
 * @param modelId - The model ID (null for LoRA models)
 * @param modelName - The model name (for fallback matching)
 */
export const useModelParameters = (modelId: string | null | undefined, modelName: string | null | undefined) => {
  const [modelParameters, setModelParameters] = useState<ApiModelParameter[] | null>(null);
  const [modelData, setModelData] = useState<ApiModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { sections } = useImageModels();

  useEffect(() => {
    const fetchModelParameters = async () => {
      setIsLoading(true);
      try {
        // OPTIMIZATION: If we have modelId, fetch directly from API (fastest path)
        // Don't wait for sections to load - this makes parameter loading instant
        if (modelId) {
          // Check cache first for instant response
          const cachedModel = modelCache.get(modelId);
          if (cachedModel) {
            setModelData(cachedModel);
            setModelParameters(cachedModel.parameters);
            setIsLoading(false);
            return; // Instant return from cache
          }

          // If not in cache, fetch from API
          try {
            const response = await api.get<ApiModel>(`/image-generation/v1/models/${modelId}`);
            // Cache the response for future use
            modelCache.set(modelId, response.data);
            setModelData(response.data);
            setModelParameters(response.data.parameters);
            setIsLoading(false);
            return; // Early return - we got the data, no need to check sections
          } catch (error) {
            console.error('Error fetching model parameters from API:', error);
            // Fall through to sections check as fallback
          }
        }

        // Fallback: Find model in sections (for cases without modelId or if API call failed)
        let foundModel: ApiModel | null = null;
        if (sections && sections.length > 0) {
          for (const section of sections) {
            const model = section.models.find(m => 
              (modelId && m.id === modelId) || 
              (modelName && m.title === modelName && m.apiModel)
            );
            if (model?.apiModel) {
              foundModel = model.apiModel;
              break;
            }
          }
        }

        if (foundModel) {
          // Cache the found model if it has an ID
          if (foundModel.id) {
            modelCache.set(foundModel.id, foundModel);
          }
          setModelData(foundModel);
          setModelParameters(foundModel.parameters);
        } else {
          // No model found
          setModelParameters(null);
          setModelData(null);
        }
      } catch (error) {
        console.error('Error in useModelParameters:', error);
        setModelParameters(null);
        setModelData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchModelParameters();
  }, [modelId, modelName, sections]);

  return { modelParameters, modelData, isLoading };
};

